"use client";

import {
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { CarouselApi } from "@/components/ui/carousel";
import { useCarouselDrag } from "@/lib/hooks/use-carousel-drag";

// Generic embla carousel helper that drives a draggable progress scrollbar and
// hides the nav arrows while the user is dragging. Shared across carousels
// (diet catalog listing, diet addons, ...).
export const useCarouselScrollbar = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isDraggingScrollbar, setIsDraggingScrollbar] = useState(false);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);

  const isDragging = useCarouselDrag(api);

  const scrollbarThumbWidth = count > 0 ? Math.max(22, 100 / count) : 100;
  const scrollbarThumbOffset =
    count > 1 ? (current / (count - 1)) * (100 - scrollbarThumbWidth) : 0;

  const scrollCarouselByPointer = useCallback(
    (clientX: number) => {
      if (!api || count <= 1 || !scrollbarTrackRef.current) {
        return;
      }

      const rect = scrollbarTrackRef.current.getBoundingClientRect();

      if (rect.width <= 0) {
        return;
      }

      const ratio = (clientX - rect.left) / rect.width;
      const clampedRatio = Math.min(1, Math.max(0, ratio));
      const targetIndex = Math.round(clampedRatio * (count - 1));

      api.scrollTo(targetIndex);
    },
    [api, count]
  );

  const handleScrollbarPointerCancel = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      setIsDraggingScrollbar(false);
    },
    []
  );

  const handleScrollbarPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsDraggingScrollbar(true);
      scrollCarouselByPointer(event.clientX);
    },
    [scrollCarouselByPointer]
  );

  const handleScrollbarPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!isDraggingScrollbar) {
        return;
      }

      scrollCarouselByPointer(event.clientX);
    },
    [isDraggingScrollbar, scrollCarouselByPointer]
  );

  const handleScrollbarPointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      setIsDraggingScrollbar(false);
    },
    []
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    const handleSelect = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };

    handleSelect();
    api.on("select", handleSelect);
    api.on("reInit", handleSelect);

    return () => {
      api.off("select", handleSelect);
      api.off("reInit", handleSelect);
    };
  }, [api]);

  return {
    count,
    handleScrollbarPointerCancel,
    handleScrollbarPointerDown,
    handleScrollbarPointerMove,
    handleScrollbarPointerUp,
    isDragging,
    scrollbarThumbOffset,
    scrollbarThumbWidth,
    scrollbarTrackRef,
    setApi,
  };
};
